// Story 7.18 (ADO-1387), AC2 — the barcode decode PORT and its browser adapter.
//
// DD-3: the decode MECHANISM is kept behind this port so the resolution + add paths
// never name the concrete library. The default adapter is the `barcode-detector`
// ponyfill (`/pure` entry) of the standard WHATWG `BarcodeDetector` Web API. The
// `/pure` entry always decodes via zxing-wasm — it does NOT delegate to a browser-
// native `BarcodeDetector` (that is the separate `barcode-detector` polyfill entry,
// which we deliberately do not import) — so the wasm mechanism runs on every browser,
// Chrome/Android included, not just iOS Safari / Firefox. A
// future app-shell bridge (mirroring SL1.0's Xamarin scanner) or a different JS lib
// can replace `createBarcodeDetectorDecoder` without touching `ScanModal`'s
// resolution/confirm/add wiring.
//
// AC2 privacy: the decoder reads pixels from the live <video> frame IN MEMORY and
// returns only the decoded digit string. NO photograph is captured, stored, or
// transmitted, and no image-recognition service is called — the only thing that
// leaves the browser is the decoded barcode string, sent to the resolution route.
//
// WASM loading (Bug #1666 — Story 7.18 Deviation D-6 resolved): the ponyfill decodes
// via the `zxing-wasm` reader binary, which by default is fetched from a jsDelivr CDN.
// The hardened SPA CSP (`nginx.conf.template`: `connect-src 'self'`, `script-src 'self'
// 'wasm-unsafe-eval'`) blocks that CDN fetch, so we serve the wasm SAME-ORIGIN: the
// build aliases the installed binary's absolute path (see `vite-zxing-wasm.ts`) and
// imports it `?url`, and `prepareZXingModule` below points the loader at that bundled
// asset. Aliasing the absolute path (rather than a `zxing-wasm/reader/...` specifier)
// is what makes `?url` resolve where D-6 failed: `zxing-wasm` is a TRANSITIVE dep, so
// its bare specifier does not resolve from `apps/web` at all. The path is derived from
// the installed package (version-synced via the lockfile, no checked-in binary).
// This wasm path runs for EVERY scanner user — the `/pure` ponyfill never uses a
// native `BarcodeDetector` — so `'wasm-unsafe-eval'` + the same-origin binary are
// universal requirements, not a fallback-browser subset. `prepareZXingModule`
// configures only the zxing-wasm loader, so it changes nothing else about decoding.

import { BarcodeDetector, prepareZXingModule } from 'barcode-detector/pure';
import zxingReaderWasmUrl from 'zxing-reader-wasm?url';

/** The three symbologies AC2 names — matching the three validators SL1.0 implements. */
export const SUPPORTED_SYMBOLOGIES = ['ean_13', 'ean_8', 'upc_a'] as const;

/**
 * The decode seam ScanModal drives. `detect` reads any barcodes visible in the given
 * frame and returns their raw decoded string values (empty when none decoded this
 * frame). It never throws for "no barcode" — an empty array means "nothing yet".
 */
export interface BarcodeDecoderPort {
  detect(source: ImageBitmapSource): Promise<readonly string[]>;
}

/** Emscripten `locateFile` for the zxing-wasm loader: the `zxing_reader.wasm` request
 *  resolves to the same-origin bundled binary (never the CDN default); everything else
 *  is left as the ponyfill would load it. Matching the concrete filename — not any
 *  `.wasm` — means a future zxing-wasm bump that loads a SECOND asset via `locateFile`
 *  fails loudly against the CDN (`connect-src 'self'`) instead of silently leaking off
 *  origin. Exported pure so the same-origin wiring is unit-testable without loading the
 *  wasm. */
export function locateZxingReaderWasm(path: string, prefix: string): string {
  return path.endsWith('zxing_reader.wasm') ? zxingReaderWasmUrl : prefix + path;
}

// Point the ponyfill's zxing-wasm loader at the same-origin bundled binary. Done
// once, lazily, on first decoder construction — module import stays side-effect-free
// so tests can import this file and inject a fake port without configuring the loader.
// `prepare` is injectable purely as a test seam: it lets the adoption test assert the
// same-origin `locateFile` override is actually handed to the loader (the load-bearing
// half of Bug #1666's fix) WITHOUT mocking `barcode-detector/pure` — a module mock
// leaks across files under this suite's `isolate: false`. Production always passes the
// real `prepareZXingModule`.
let wasmLoaderConfigured = false;
function configureWasmLoaderOnce(prepare: typeof prepareZXingModule): void {
  if (wasmLoaderConfigured) return;
  prepare({ overrides: { locateFile: locateZxingReaderWasm } });
  wasmLoaderConfigured = true;
}

/** Reset the once-latch. TEST ONLY — so the loader-adoption assertion can re-run the
 *  first-construction path under `isolate: false` (module state persists across specs). */
export function resetWasmLoaderForTest(): void {
  wasmLoaderConfigured = false;
}

/** Create the default browser decoder (the ponyfill). Isolated so tests inject a fake
 *  `BarcodeDecoderPort` and never load the wasm. `prepare` defaults to the real
 *  `prepareZXingModule`; it is an injectable test seam (see `configureWasmLoaderOnce`). */
export function createBarcodeDetectorDecoder(
  prepare: typeof prepareZXingModule = prepareZXingModule,
): BarcodeDecoderPort {
  configureWasmLoaderOnce(prepare);
  const detector = new BarcodeDetector({ formats: [...SUPPORTED_SYMBOLOGIES] });
  return {
    async detect(source) {
      const results = await detector.detect(source);
      return results.map((r) => r.rawValue);
    },
  };
}
